import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /**
   * How many sibling pages to show on each side of the current page.
   * Default 1 → renders `prev — first — … — siblings — current — siblings — … — last — next`.
   */
  siblingCount?: number;
  className?: string;
}

// Builds the page list with ellipsis markers. Returns numbers for real
// pages and the sentinel strings 'left-gap' / 'right-gap' so the
// renderer can drop a non-interactive "…" tile in those slots.
function buildPageRange(
  total: number,
  current: number,
  siblings: number,
): Array<number | 'left-gap' | 'right-gap'> {
  // Show every page when the list is short enough that the ellipsis
  // would only save one or two buttons (cleaner with no gaps).
  const SHOW_ALL_THRESHOLD = siblings * 2 + 5;
  if (total <= SHOW_ALL_THRESHOLD) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblings, 1);
  const rightSibling = Math.min(current + siblings, total);

  const showLeftGap = leftSibling > 3;
  const showRightGap = rightSibling < total - 2;

  const out: Array<number | 'left-gap' | 'right-gap'> = [];

  if (!showLeftGap && showRightGap) {
    // Current page is near the start — keep the first run dense.
    const leftCount = 3 + 2 * siblings;
    for (let i = 1; i <= leftCount; i++) out.push(i);
    out.push('right-gap');
    out.push(total);
  } else if (showLeftGap && !showRightGap) {
    // Current page is near the end — keep the tail dense.
    out.push(1);
    out.push('left-gap');
    const rightCount = 3 + 2 * siblings;
    for (let i = total - rightCount + 1; i <= total; i++) out.push(i);
  } else if (showLeftGap && showRightGap) {
    // Middle — first, gap, siblings around current, gap, last.
    out.push(1);
    out.push('left-gap');
    for (let i = leftSibling; i <= rightSibling; i++) out.push(i);
    out.push('right-gap');
    out.push(total);
  } else {
    // Both flanks short enough to render in full.
    for (let i = 1; i <= total; i++) out.push(i);
  }

  return out;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className,
}) => {
  if (totalPages <= 1) return null;

  const pages = buildPageRange(totalPages, currentPage, siblingCount);
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const tileBase =
    "min-w-[40px] h-10 px-3 rounded-[8px] flex items-center justify-center font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors";

  return (
    <div
      className={`flex items-center gap-1.5 pt-4 flex-wrap${
        className ? ` ${className}` : ''
      }`}
    >
      <button
        type="button"
        onClick={() => canPrev && onPageChange(currentPage - 1)}
        disabled={!canPrev}
        aria-label="Previous page"
        className={`${tileBase} text-[#908e8e] hover:bg-[#fcfaf7] hover:text-[#3a3335] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#908e8e]`}
      >
        <span className="material-icons text-[18px]">chevron_left</span>
      </button>

      {pages.map((p, idx) => {
        if (p === 'left-gap' || p === 'right-gap') {
          return (
            <span
              key={`${p}-${idx}`}
              className={`${tileBase} text-[#908e8e] cursor-default`}
              aria-hidden="true"
            >
              …
            </span>
          );
        }
        const isActive = p === currentPage;
        return (
          <button
            type="button"
            key={p}
            onClick={() => onPageChange(p)}
            aria-current={isActive ? 'page' : undefined}
            className={`${tileBase} ${
              isActive
                ? 'bg-[#8e2a2f] text-white'
                : 'text-[#908e8e] hover:bg-[#fcfaf7] hover:text-[#3a3335]'
            }`}
          >
            {p}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => canNext && onPageChange(currentPage + 1)}
        disabled={!canNext}
        aria-label="Next page"
        className={`${tileBase} text-[#908e8e] hover:bg-[#fcfaf7] hover:text-[#3a3335] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#908e8e]`}
      >
        <span className="material-icons text-[18px]">chevron_right</span>
      </button>
    </div>
  );
};
